import { type DefaultError, skipToken, useMutation, useQuery } from '@tanstack/react-query';
import {
  createAsset as createAssetQuery,
  createAssetVersion as createAssetVersionQuery,
  getAsset,
} from '@/services/asset.service';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { QueryKey } from '@/lib/queryKeys';
import { type Asset } from '@/types/asset';

export const useAsset = (
  id?: string,
  options?: {
    refetchOnWindowFocus?: boolean;
    refetchInterval?: number | false;
    enabled?: boolean;
    /** When true, a failed fetch is absorbed: no global error toast, no console log. */
    suppressGlobalError?: boolean;
  },
) => {
  const { isPublicSharePage } = useCurrentPage();

  const assetsQueryFn = useAuthenticatedQueryFn(getAsset);
  const createAssetFn = useAuthenticatedQueryFn(createAssetQuery);
  const createAssetVersionFn = useAuthenticatedQueryFn(createAssetVersionQuery);

  const { refetchOnWindowFocus = true, enabled = true, suppressGlobalError = false, ...restOptions } = options ?? {};

  const {
    data: asset,
    isLoading: isAssetLoading,
    isPending: isAssetPending,
    isFetching: isAssetFetching,
    error: assetError,
    refetch: refetchAsset,
  } = useQuery({
    queryKey: [QueryKey.Asset, id ?? undefined],
    queryFn: id
      ? async () => {
          const data = isPublicSharePage
            ? skipToken
            : await assetsQueryFn({ id: id ?? undefined, silent: suppressGlobalError });
          return data;
        }
      : skipToken,
    enabled: enabled,
    refetchOnWindowFocus,
    ...(suppressGlobalError && { meta: { suppressGlobalError: true } }),
    ...restOptions,
  });

  const { mutateAsync: createAssetMutation } = useMutation<
    Asset,
    DefaultError,
    Parameters<typeof createAssetQuery>['0']
  >({
    mutationFn: (params) => createAssetFn(params),
  });

  const {
    mutateAsync: createAssetVersion,
    isPending: isUploadVersionPending,
    error: uploadVersionError,
  } = useMutation<Asset, DefaultError, Parameters<typeof createAssetVersionQuery>['0']>({
    mutationFn: (params) => createAssetVersionFn(params),
  });

  return {
    asset,
    refetchAsset,
    createAssetMutation,
    createAssetVersion,
    isAssetFetching,
    isAssetLoading,
    isPending: isAssetPending || isUploadVersionPending,
    error: assetError ?? uploadVersionError,
  };
};
